import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Phone, User, MessageSquare } from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { useSession } from '../contexts/SessionContext'
import { useLanguage } from '../contexts/LanguageContext'
import { customerApi } from '../services/api'
import { addRecentOrder } from '../utils/recentActivity'

export default function Checkout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { items, getSubtotal, clearCart } = useCart()
  const { formatPrice, orderPlacingEnabled, taxRate, serviceChargeRate } = useRestaurant()
  const { createSession } = useSession()
  const { t, isRTL } = useLanguage()
  const state = location.state as { customerName?: string; customerMobile?: string; orderNotes?: string } | null
  const [customerName, setCustomerName] = useState(state?.customerName ?? '')
  const [customerMobile, setCustomerMobile] = useState(state?.customerMobile ?? '')
  const [tableNumber, setTableNumber] = useState('')
  const [notes, setNotes] = useState(state?.orderNotes ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const subtotal = getSubtotal()
  const vatAmount = (subtotal * taxRate) / 100
  const service = (subtotal * serviceChargeRate) / 100
  const total = subtotal + vatAmount + service

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!customerMobile.trim()) {
      setError('Mobile number is required')
      return
    }

    // Basic mobile number validation
    const mobileRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/
    if (!mobileRegex.test(customerMobile.trim())) {
      setError('Please enter a valid mobile number')
      return
    }

    setLoading(true)

    try {
      const orderItems = items.map(item => ({
        menu_item_id: item.menu_item_id,
        variant_name: item.variant_name,
        addons: item.addons || [],
        quantity: item.quantity,
        notes: item.notes
      }))

      const response = await customerApi.createOrder({
        items: orderItems,
        customer_mobile: customerMobile.trim(),
        customer_name: customerName.trim() || undefined,
        notes: notes.trim() || undefined,
        table_number: tableNumber.trim() || undefined
      })

      if (response.success && response.data) {
        const orderData = response.data as {
          order_number?: string
          accessToken?: string
          total_amount?: number
          created_at?: string
          status?: string
        }
        const orderNumber = orderData.order_number ?? (orderData as any).orderNumber
        if (orderNumber) {
          addRecentOrder({
            order_number: String(orderNumber),
            customer_mobile: customerMobile.trim(),
            total_amount: Number(orderData.total_amount) ?? 0,
            created_at: orderData.created_at ?? new Date().toISOString(),
            status: orderData.status ?? 'pending'
          })
        }
        // Create session with access token if provided
        if (orderData.accessToken) {
          createSession(customerMobile.trim(), orderData.accessToken)
        }
        clearCart()
        navigate(`/order-confirmation/${orderData.order_number ?? orderNumber ?? ''}`)
      } else {
        setError(response.error || 'Failed to place order. Please try again.')
      }
    } catch (err) {
      setError('Failed to place order. Please try again.')
      console.error('Error placing order:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-amber-500"
          >
            <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
            <span>Back</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Checkout</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Customer Information</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value)}
                  placeholder="+1234567890"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Name (Optional)
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Your name"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Table Number (Optional)
                </label>
                <input
                  type="text"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  placeholder="Table number if dining in"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Special Instructions (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests or instructions?"
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-2 mb-4">
              {items.map((item, index) => {
                const unitPrice = item.base_price + (item.variant_price_modifier || 0) + (item.addons_price || 0)
                const itemTotal = unitPrice * item.quantity
                return (
                  <div key={index} className="flex justify-between text-gray-600">
                    <span>{item.name} × {item.quantity}</span>
                    <span>{formatPrice(itemTotal)}</span>
                  </div>
                )
              })}
            </div>
            <div className={`border-t pt-4 space-y-2 ${isRTL ? 'text-right' : ''}`}>
              <div className="flex justify-between text-gray-600">
                <span>{t('subtotal')}</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>{t('vat')} ({taxRate}%)</span>
                <span>{formatPrice(vatAmount)}</span>
              </div>
              {service > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>{t('serviceCharge')}</span>
                  <span>{formatPrice(service)}</span>
                </div>
              )}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-900">{t('total')}</span>
                  <span className="text-2xl font-bold text-amber-500">{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Place Order: only when ordering is enabled */}
          {orderPlacingEnabled ? (
            <button
              type="submit"
              disabled={loading || items.length === 0}
              className="w-full bg-amber-500 text-white py-4 rounded-xl font-semibold text-lg hover:bg-amber-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Placing Order...' : 'Place Order'}
            </button>
          ) : (
            <div className="bg-gray-100 border border-gray-200 text-gray-600 px-4 py-3 rounded-lg text-center">
              Ordering is not available. Your cart is for reference—please order with staff.
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

